import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Inbox } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Cáscara base compartida por las 4 columnas del kanban LCL.
 *
 * Props:
 *  - id:         string único de la columna (id del bucket)
 *  - title:      título del header (ej. "Cotizaciones")
 *  - accent:     clase Tailwind del borde superior de color
 *  - badge:      clase Tailwind del badge contador
 *  - items:      array de filas a renderizar
 *  - renderItem: (item, idx) => ReactNode — cómo dibujar cada tarjeta
 *  - loading:    boolean — muestra "Cargando..." en el estado vacío
 *  - emptyText:  texto cuando no hay items
 *  - onAdd?:     () => void — si está, muestra un botón "+" en el header
 *  - headerExtra: ReactNode extra en el header (ej. "Ver tablero →")
 */
const LclKanbanColumn = ({
  id,
  title,
  accent = 'border-t-slate-400',
  badge = 'bg-slate-100 text-slate-800 border-slate-200',
  items = [],
  renderItem,
  loading = false,
  emptyText = 'Sin elementos',
  onAdd,
  headerExtra,
}) => {
  return (
    <div
      className={cn(
        'flex flex-col rounded-lg border border-t-4 bg-muted/20 min-h-[200px] max-h-[calc(100vh-280px)]',
        accent,
      )}
      data-column-id={id}
    >
      {/* Header de columna */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-card/60 rounded-t-md gap-2">
        <h3 className="text-sm font-semibold text-foreground truncate">{title}</h3>
        <div className="flex items-center gap-2 shrink-0">
          {headerExtra}
          {onAdd && (
            <button
              type="button"
              onClick={onAdd}
              className="h-6 w-6 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex items-center justify-center"
              aria-label={`Añadir a ${title}`}
              title={`Añadir a ${title}`}
            >
              <span className="text-base leading-none">+</span>
            </button>
          )}
          <span
            className={cn(
              'text-[10px] font-semibold px-1.5 py-0.5 rounded-full border',
              badge,
            )}
          >
            {items.length}
          </span>
        </div>
      </div>

      {/* Lista scrollable */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2 custom-scrollbar">
        <AnimatePresence initial={false}>
          {items.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center text-center text-muted-foreground py-8"
            >
              <Inbox className="h-8 w-8 mb-1 opacity-50" />
              <p className="text-xs">{loading ? 'Cargando...' : emptyText}</p>
            </motion.div>
          ) : (
            items.map((item, idx) => (
              <React.Fragment key={item?._id || item?.codigo || idx}>
                {renderItem ? renderItem(item, idx) : null}
              </React.Fragment>
            ))
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LclKanbanColumn;
